import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import { PageError, loadError } from '../components/OfflineNotice';

const KPI_META = {
  revenue:      { label: 'Total Revenue',  color: () => 'inherit' },
  gross_profit: { label: 'Gross Profit',   color: (v) => v >= 0 ? 'var(--color-success)' : 'var(--color-danger)' },
  net_profit:   { label: 'Net Profit',     color: (v) => v >= 0 ? 'var(--color-success)' : 'var(--color-danger)' },
  receivables:  { label: 'Receivables',    color: () => 'var(--color-warning)' },
};

function bizValue(b, key) {
  const rev   = Number(b.revenue);
  const gross = rev - Number(b.cogs);
  const net   = gross - Number(b.expenses);
  return { revenue: rev, gross_profit: gross, net_profit: net, receivables: Number(b.receivables) }[key] ?? 0;
}

export default function CompanyKpiDetail() {
  const { type }          = useParams();
  const navigate          = useNavigate();
  const { parentCompany } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const cur  = parentCompany?.currency || '';
  const meta = KPI_META[type] || { label: type, color: () => 'inherit' };

  const fmt = (n) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  useEffect(() => {
    companyAPI.financials()
      .then(r => setBusinesses(r.data.businesses || []))
      .catch((err) => setError(loadError(err, 'Failed to load data')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page"><BackButton /><p className="text-muted">Loading…</p></div>;
  if (error)   return <div className="page"><BackButton /><PageError error={error} /></div>;

  const total = businesses.reduce((s, b) => s + bizValue(b, type), 0);

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">{meta.label}</h1>
      <p className="text-muted" style={{ fontSize: '0.82rem', marginBottom: '1.25rem' }}>
        {parentCompany?.name} · breakdown by business
      </p>

      {/* Total summary card */}
      <div className="card" style={{ marginBottom: '1.25rem', textAlign: 'center', padding: '1.25rem 1rem' }}>
        <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.3rem' }}>
          Combined {meta.label}
        </div>
        <div style={{ fontSize: '1.75rem', fontWeight: 700, color: meta.color(total) }}>
          {cur} {fmt(total)}
        </div>
      </div>

      {/* Per-business breakdown */}
      {businesses.length === 0 && <p className="text-muted">No businesses found.</p>}

      {businesses.map(b => {
        const val = bizValue(b, type);
        const pct = total !== 0 ? Math.abs((val / total) * 100) : 0;
        const col = meta.color(val);

        return (
          <div
            key={b.id}
            className="card"
            style={{ marginBottom: '0.6rem', cursor: 'pointer' }}
            onClick={() => navigate(`/company/business/${b.id}`)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>{b.name}</span>
              <span style={{ fontWeight: 700, fontSize: '0.92rem', color: col, flexShrink: 0, marginLeft: '0.75rem' }}>
                {cur} {fmt(val)}
              </span>
            </div>

            {/* Progress bar */}
            <div style={{ height: '4px', borderRadius: '3px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.min(pct, 100)}%`,
                background: col === 'inherit' ? '#6366f1' : col,
                borderRadius: '3px',
                transition: 'width 0.3s',
                minWidth: pct > 0 ? '4px' : '0',
              }} />
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', marginTop: '0.3rem' }}>
              {pct.toFixed(1)}% of total
            </div>
          </div>
        );
      })}
    </div>
  );
}
